import { memo, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TableCell,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { LockIcon } from 'lucide-react';
import {
  isFieldEdited,
  type JobFieldValue,
  type JobIssue,
  type PlatformFieldDef,
} from '@/entities/media-campaign';
import { isFindingOutOfDate, type PreflightStatus } from '../lib/preflight';

const EM_DASH = '—';

export interface JobTableCellProps {
  def: PlatformFieldDef;
  field: JobFieldValue | undefined;
  editable: boolean;
  issue: JobIssue | undefined;
  width: number;
  /** `nodeId:fieldKey`, the address the issues drawer scrolls to. */
  cellId: string;
  isFocused: boolean;
  preflightStatus: PreflightStatus;
  /** The node this cell belongs to, so the commit callback can stay shared. */
  nodeId: string;
  /**
   * Shared by every cell rather than bound per cell: a closure per cell would
   * change identity on each parent render and defeat the memo below, which is
   * what keeps a column drag from re-rendering the whole sheet.
   */
  onCommit: (nodeId: string, fieldKey: string, value: string | null) => void;
  /**
   * Pins the cell this many pixels from the left of the scroll region.
   *
   * Set for the name column, which stays on screen while the sheet scrolls. It
   * reuses this component rather than rendering its own editor, so a name is
   * corrected exactly the way every other field is.
   */
  stickyLeft?: number;
  /** Extra padding for the name column when the grouped view indents it. */
  paddingLeft?: number;
  /**
   * Pinned to the right of the cell's own content.
   *
   * The issue badge lives here rather than in a column of its own. A whole column
   * for a counter that is empty on most rows spent 52px of a sheet that is already
   * wider than any screen, and read as a blank gap between the name and everything
   * else — where the badge is about the row, and belongs beside its name.
   */
  adornment?: ReactNode;
  /** Shown instead of the plan's value, once the platform has said what it holds. */
  platformValue?: string;
}

function optionLabel(def: PlatformFieldDef, value: string): string {
  return def.options?.find((option) => option.value === value)?.label ?? value;
}

/**
 * One cell of the trafficking sheet.
 *
 * Click to edit rather than rendering a control per cell: a Meta ad row carries
 * 27 fields, and a grid of that many permanently-open inputs is unreadable —
 * which is the whole reason this replaced the nested tree.
 */
function JobTableCellImpl({
  def,
  field,
  editable,
  issue,
  width,
  cellId,
  isFocused,
  preflightStatus,
  nodeId,
  onCommit,
  stickyLeft,
  paddingLeft,
  adornment,
  platformValue,
}: JobTableCellProps) {
  const isSticky = stickyLeft !== undefined;
  const ref = useRef<HTMLTableCellElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(field?.value ?? '');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isEditing) setDraft(field?.value ?? '');
  }, [field?.value, isEditing]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  // Centred rather than nudged into view: on a sheet this wide the cell can be
  // thousands of pixels off to the right, and landing it at the very edge would
  // leave the reviewer hunting for what just moved.
  useEffect(() => {
    if (isFocused) ref.current?.scrollIntoView({ block: 'center', inline: 'center' });
  }, [isFocused]);

  const value = field?.value ?? null;
  const edited = field !== undefined && isFieldEdited(field);
  // A finding quoting a value the operator has since changed must stop tinting
  // the cell; it is a note to re-check, not a current fact.
  const outOfDate = isFindingOutOfDate(preflightStatus, edited);
  const hasError = issue?.severity === 'error' && !outOfDate;
  const hasWarning = issue?.severity === 'warning' && !outOfDate;

  const commit = () => {
    setIsEditing(false);
    const next = draft.trim();
    if (next === (value ?? '')) return;
    onCommit(nodeId, def.key, next === '' ? null : next);
  };

  const isEmpty = value === null || value === '';
  const emptyTone = hasError ? 'text-destructive' : 'text-muted-foreground';
  const display = platformValue ?? (isEmpty ? EM_DASH : optionLabel(def, value));

  const body = () => {
    if (!editable) {
      return (
        <span className="flex min-w-0 items-center gap-1 text-muted-foreground">
          <LockIcon className="size-3 shrink-0" aria-hidden />
          <span className="min-w-0 truncate">{display}</span>
        </span>
      );
    }

    if (isEditing && def.kind === 'select' && def.options) {
      return (
        <Select
          open
          value={value ?? ''}
          onValueChange={(next) => {
            setIsEditing(false);
            onCommit(nodeId, def.key, next === '' ? null : next);
          }}
          onOpenChange={(open) => !open && setIsEditing(false)}
        >
          <SelectTrigger size="mini" className="w-full border-none bg-transparent px-0">
            <SelectValue placeholder={EM_DASH} />
          </SelectTrigger>
          <SelectContent>
            {def.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (isEditing) {
      return (
        <input
          ref={inputRef}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit();
            else if (event.key === 'Escape') {
              setDraft(value ?? '');
              setIsEditing(false);
            }
          }}
          className="w-full rounded border border-primary bg-background px-1 py-0.5 text-sm outline-none"
        />
      );
    }

    return (
      <button
        type="button"
        onClick={() => setIsEditing(true)}
        className="block w-full max-w-full cursor-text overflow-hidden rounded px-1 py-0.5 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        title={value ?? undefined}
      >
        <span className={`block truncate ${isEmpty ? emptyTone : ''}`}>{display}</span>
      </button>
    );
  };

  /**
   * A pinned cell paints an opaque base and layers the tint over it.
   *
   * The tint is 5% alpha, so using it as the cell's own background replaces that
   * base and the scrolling columns show straight through — which is what an empty
   * required cell looked like, since being empty is exactly when it is tinted.
   *
   * No `relative` on the sticky branch: `sticky` is already a positioned element,
   * so it is the containing block for the overlay, and a second `position` would
   * cost the cell its stickiness.
   *
   * Written out in full rather than composed from parts: Tailwind only emits
   * classes it can read literally in the source.
   */
  const OVERLAY = "before:absolute before:inset-0 before:content-['']";
  const background = isSticky
    ? hasError
      ? `sticky bg-background group-hover/row:bg-accent ${OVERLAY} before:bg-destructive/5`
      : edited
        ? `sticky bg-background group-hover/row:bg-accent ${OVERLAY} before:bg-primary/5`
        : 'sticky bg-background group-hover/row:bg-accent'
    : hasError
      ? 'bg-destructive/5'
      : edited
        ? 'bg-primary/5'
        : '';

  return (
    // `overflow-hidden` is load-bearing under `table-layout: fixed`: without it a
    // long value paints straight over the neighbouring column.
    <TableCell
      ref={ref}
      style={{
        width: `${width}px`,
        ...(isSticky ? { left: `${stickyLeft}px` } : {}),
        ...(paddingLeft !== undefined ? { paddingLeft: `${paddingLeft}px` } : {}),
      }}
      data-job-cell={cellId}
      // A pinned cell has to outrank the scrolling ones — including while focused,
      // where a scrolling cell lifts itself to z-[5].
      className={`border-b border-r border-border px-2 py-1.5 align-middle text-sm before:pointer-events-none ${background} ${
        isFocused
          ? `overflow-visible outline outline-2 outline-primary ${
              isSticky ? 'z-20' : 'relative z-[5]'
            }`
          : `overflow-hidden ${isSticky ? 'z-10' : ''}`
      }`}
      data-testid={`job-cell-${def.key}`}
    >
      <div className="flex min-w-0 items-center gap-1">
        {issue ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="block min-w-0 flex-1 overflow-hidden">{body()}</span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[20rem]">{issue.message}</TooltipContent>
          </Tooltip>
        ) : (
          <span className="block min-w-0 flex-1 overflow-hidden">{body()}</span>
        )}
        {(hasError || hasWarning) && (
          <span
            aria-hidden
            className={`size-1.5 shrink-0 rounded-full ${hasError ? 'bg-destructive' : 'bg-warning-foreground'}`}
          />
        )}
        {adornment}
      </div>
    </TableCell>
  );
}

/**
 * Memoized because the sheet is a few thousand cells and the parent re-renders
 * at pointer rate while a column is resized. Every prop is a primitive or a
 * value that only changes when this cell's own data does.
 */
export const JobTableCell = memo(JobTableCellImpl);
